import * as React from 'react';
import cx from 'classnames';
import { useLocation, useNavigate } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import { styled, Theme, CSSObject } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { ReactComponent as LogoIcon } from './images/logo.svg';
import { ReactComponent as DashboardIcon } from './images/dashboard.svg';
import { ReactComponent as PaymentIcon } from './images/payment.svg';
import { ReactComponent as IntegrationIcon } from './images/integration.svg';
import { ReactComponent as SettingIcon } from './images/settings.svg';
import { ReactComponent as LogoutIcon } from './images/logout.svg';

import css from './MiniDrawer.module.scss';

const drawerWidth = 266;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(8)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  variants: [
    {
      props: ({ open }) => open,
      style: {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': openedMixin(theme),
      },
    },
    {
      props: ({ open }) => !open,
      style: {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': closedMixin(theme),
      },
    },
  ],
}));

export default function MiniDrawer({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const handleDrawerOpen = () => {
    setOpen(true);
  };
  const handleDrawerClose = () => {
    setOpen(false);
  };

  const { keycloak } = useKeycloak();

  const LIST = [
    {
      icon: <LogoIcon />,
      text: 'FINTEGRATOR',
      url: undefined,
    },
    {
      icon: <DashboardIcon />,
      text: 'Dashboard',
      url: '/',
    },
    {
      icon: <PaymentIcon />,
      text: 'Payments',
      url: '/payments',
    },
    {
      icon: <IntegrationIcon />,
      text: 'Integration',
      url: '/integration',
    },
    {
      icon: <SettingIcon />,
      text: 'Settings',
      url: '/settings',
    },
    {
      icon: <LogoutIcon />,
      text: 'Logout',
      onClick: React.useCallback(() => {
        keycloak.logout();
      }, [keycloak]),
    },
  ];

  return (
    <Box sx={{ display: 'flex' }}>
      <Drawer variant="permanent" open={open}>
        <div onMouseOver={handleDrawerOpen} onMouseOut={handleDrawerClose}>
          <List className={css.list}>
            {LIST.map((item, index) => (
              <ListItem
                className={cx(!item.url && css.isLogo, item.url === pathname ? css.isSelected : undefined)}
                sx={{
                  display: 'block',
                  ...(item.text === 'Logout' ? { position: 'absolute', bottom: 0 } : null),
                  // selected and (selected + hover) states
                  '&& .Mui-selected, && .Mui-selected:hover': {
                    ...(item.url ? { bgcolor: '#F5FBFF' } : { bgcolor: '#fff !important' }),
                  },
                  // hover states
                  '& .MuiListItemButton-root:hover': {
                    ...( item.url ? { bgcolor: '#F5FBFF' } : { bgcolor: '#fff !important' }),
                    '&, & .MuiListItemIcon-root': {
                      ...(item.url ? { color: '#0000FF' } : { cursor: 'default' }),
                    },
                  },
                  '& .MuiListItemButton-root:hover svg path, & .MuiListItemButton-root:hover svg rect, & .MuiListItemButton-root:hover svg circle':
                    index !== 0
                      ? {
                          stroke: '#0000FF',
                        }
                      : {},
                  '& .MuiListItemButton-root': {
                    margin: '5px 10px',
                    borderRadius: '10px',
                    ...(item.url === pathname ? { bgcolor: '#F5FBFF' } : {}),
                  },
                  '& .MuiTouchRipple-child': {
                    ...( !item.url ? { bgcolor: '#fff' } : {}),
                  },
                }}
                key={item.text}
                disablePadding
                onClick={() => {
                  if (item.onClick) {
                    item.onClick();
                  } else if (item.url) {
                    navigate(item.url);
                  }
                }}
              >
                <ListItemButton
                  sx={[
                    {
                      minHeight: 48,
                      px: 2.5,
                    },
                    open
                      ? {
                          justifyContent: 'initial',
                        }
                      : {
                          justifyContent: 'center',
                        },
                  ]}
                >
                  <ListItemIcon
                    className={css.icon}
                    sx={[
                      {
                        justifyContent: 'center',
                      },
                      open
                        ? {
                            mr: 3,
                          }
                        : {
                            mr: 'auto',
                          },
                    ]}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    className={cx(
                      index === 0 ? css.bold : undefined,
                      item.url && item.url === pathname ? css.isSelected : undefined
                    )}
                    primary={item.text}
                    sx={[
                      open
                        ? {
                            opacity: 1,
                          }
                        : {
                            opacity: 0,
                          },
                    ]}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </div>
      </Drawer>
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        {children}
      </Box>
    </Box>
  );
}
